import React from 'react';
import NewModelSpecsGrid from './NewModelSpecsGrid';
import { getBrandLogo } from './utils/brandLogo';
import { useTranslation } from '../../i18n/I18nContext';

/**
 * Bottom glass card on the new-model landing — title, optional inline
 * price ("À partir de €..."), 4 spec cards, and the CTA button.
 *
 * @param {object} props
 * @param {object} props.car — Car object (DealerCarDto).
 * @param {string} props.title — display title (e.g. "OMODA 5 SHS").
 * @param {number|null|undefined} props.displayPrice — already-resolved price
 *   (incl. duty-free if user toggled it on parent).
 * @param {(price: number|null|undefined) => string} props.formatPrice — price
 *   formatter. Reused so currency/locale stays consistent.
 * @param {() => void} props.onCta — fired on CTA click.
 */
const GlassInfoCard = ({ car, title, displayPrice, formatPrice, onCta }) => {
  const { t } = useTranslation();
  const ctaLabel = t('landing.cta.learnMore');
  const promoOrRegular = car.promoPrice || displayPrice;
  // Лого производителя (OMODA / JAECOO) — переехало с фото на верх карточки,
  // по центру, над названием и ценой (Маша). Логотип Autodis теперь в хедере. */
  const { src: brandSrc, alt: brandAlt } = getBrandLogo(car);
  // The whole header is keyed by car.id so the title + price-inline
  // remount as a unit on car switch — React reconciles them as a fresh
  // subtree and the `landing-card-in` keyframe (see NewModelLanding.css)
  // replays. Keying individual siblings with the same `key={car.id}`
  // was a duplicate-key bug: React couldn't tell two same-keyed
  // siblings apart on swipe, so the old h1 stayed in DOM and titles
  // piled up across navigations (#965 user feedback).
  return (
    <div className="landing__card">
      {brandSrc && (
        <div className="landing__card-brandrow">
          <span className="landing__card-brand-divider" aria-hidden="true" />
          <span className="landing__card-brand-frame">
            <img className="landing__card-brand" src={brandSrc} alt={brandAlt} />
          </span>
          <span className="landing__card-brand-divider" aria-hidden="true" />
        </div>
      )}
      <div key={car.id} className="landing__card-header">
        <h1 className="landing__title">{title}</h1>
        {displayPrice > 0 && (
          <div className="landing__price-inline">
            <span className="landing__price-inline-label">{t('print.fromPrice')}</span>
            <span className="landing__price-inline-amount">
              {formatPrice(promoOrRegular)}
            </span>
          </div>
        )}
      </div>

      <NewModelSpecsGrid car={car} />

      <button
        type="button"
        className="landing__cta"
        onClick={onCta}
        aria-label={ctaLabel}
      >
        {ctaLabel}
      </button>
    </div>
  );
};

export default GlassInfoCard;
